import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { supabase, getFlightBookings, getUserById } from './supabaseClient';
import './Bookings.css';

const Bookings = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingStatusFilter, setBookingStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch real bookings data from Supabase
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all flight bookings with related data
        const bookingsData = await getFlightBookings();

        // Fetch user information for each booking
        const bookingsWithUsers = await Promise.all(
          bookingsData.map(async (booking) => {
            let userName = 'Unknown User';
            let userEmail = booking.contact_email || 'N/A';
            
            // Check if user data is already in the booking (from join)
            if (booking.users && Array.isArray(booking.users) && booking.users.length > 0) {
              const user = booking.users[0];
              userName = user.name || user.email || 'Unknown User';
              userEmail = user.email || booking.contact_email || 'N/A';
            } else if (booking.user_id) {
              // Fallback: fetch user separately if not in join
              try {
                const user = await getUserById(booking.user_id);
                if (user) {
                  userName = user.name || user.email || 'Unknown User';
                  userEmail = user.email || booking.contact_email || 'N/A';
                }
              } catch (err) {
                console.warn('Could not fetch user for booking:', booking.id, err);
              }
            }

            // Format route information
            const outboundRoute = booking.outbound_departure_city && booking.outbound_arrival_city
              ? `${booking.outbound_departure_city} → ${booking.outbound_arrival_city}`
              : booking.outbound_departure_airport && booking.outbound_arrival_airport
              ? `${booking.outbound_departure_airport} → ${booking.outbound_arrival_airport}`
              : 'N/A';

            // Format flight number
            const flightNumber = booking.outbound_flight_number || 'N/A';

            // Format amount
            const amount = booking.total_amount
              ? `₦${parseFloat(booking.total_amount).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '₦0.00';

            // Format booking date
            const bookingDate = booking.booking_date
              ? new Date(booking.booking_date).toLocaleDateString('en-NG', { year: 'numeric', month: '2-digit', day: '2-digit' })
              : 'N/A';

            // Get passenger count
            const passengerCount = Array.isArray(booking.booking_passengers) 
              ? booking.booking_passengers.length 
              : (booking.number_of_passengers || 0);

            // Get payment method
            const paymentMethod = Array.isArray(booking.booking_payments) && booking.booking_payments.length > 0
              ? booking.booking_payments[0].payment_method 
              : 'N/A';

            return {
              id: booking.booking_reference || booking.id,
              bookingId: booking.id,
              userId: booking.user_id,
              userName: userName,
              userEmail: userEmail,
              route: outboundRoute,
              flightNumber: flightNumber,
              bookingDate: bookingDate,
              amount: amount,
              bookingStatus: booking.booking_status ? booking.booking_status.charAt(0).toUpperCase() + booking.booking_status.slice(1) : 'Pending',
              paymentStatus: booking.payment_status ? booking.payment_status.charAt(0).toUpperCase() + booking.payment_status.slice(1) : 'Unpaid',
              passengerCount: passengerCount,
              contactEmail: booking.contact_email || 'N/A',
              contactPhone: booking.contact_phone || 'N/A',
              cabinClass: booking.cabin_class || 'economy',
              flightType: booking.flight_type || 'one-way',
              outboundDeparture: booking.outbound_departure_date 
                ? new Date(booking.outbound_departure_date).toLocaleDateString('en-NG')
                : 'N/A',
              outboundDepartureTime: booking.outbound_departure_time || 'N/A',
              outboundArrival: booking.outbound_arrival_date
                ? new Date(booking.outbound_arrival_date).toLocaleDateString('en-NG')
                : 'N/A',
              outboundArrivalTime: booking.outbound_arrival_time || 'N/A',
              returnDeparture: booking.return_departure_date
                ? new Date(booking.return_departure_date).toLocaleDateString('en-NG')
                : null,
              returnArrival: booking.return_arrival_date
                ? new Date(booking.return_arrival_date).toLocaleDateString('en-NG')
                : null,
              baseFare: booking.base_fare || 0,
              taxes: booking.taxes || 0,
              serviceFee: booking.service_fee || 0,
              currency: booking.currency || 'NGN',
              paymentMethod: paymentMethod,
              rawBooking: booking // Keep raw data for details view
            };
          })
        );

        setAllBookings(bookingsWithUsers);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError(err.message || 'Failed to fetch bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Only rows from Supabase (getFlightBookings → flight_bookings + relations)
  const bookingsToDisplay = allBookings;

  // Filter bookings based on search and filters
  const filteredBookings = bookingsToDisplay.filter(booking => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      String(booking.id).toLowerCase().includes(q) ||
      String(booking.userName || '').toLowerCase().includes(q) ||
      String(booking.userEmail || '').toLowerCase().includes(q) ||
      String(booking.contactEmail || '').toLowerCase().includes(q) ||
      String(booking.contactPhone || '').toLowerCase().includes(q);
    
    const matchesBookingStatus = bookingStatusFilter === 'all' || 
      booking.bookingStatus.toLowerCase() === bookingStatusFilter.toLowerCase();
    
    const matchesPaymentStatus = paymentStatusFilter === 'all' || 
      booking.paymentStatus.toLowerCase() === paymentStatusFilter.toLowerCase();
    
    return matchesSearch && matchesBookingStatus && matchesPaymentStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredBookings.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleBookingStatusFilterChange = (e) => {
    setBookingStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePaymentStatusFilterChange = (e) => {
    setPaymentStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting bookings to CSV...');
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedBooking(null);
  };

  const handleCancelBooking = (bookingId) => {
    // Placeholder for cancel booking
    console.log('Cancel booking:', bookingId);
  };

  const handleReschedule = (bookingId) => {
    // Placeholder for reschedule
    console.log('Reschedule booking:', bookingId);
  };

  return (
    <AdminLayout pageTitle="Bookings">
      <div className="admin-bookings-page">
        {/* Page Title */}
        <div className="admin-bookings-header">
          <h1 className="admin-bookings-title">Bookings</h1>
          <p className="admin-bookings-subtitle">Manage all flight bookings</p>
        </div>

        {/* Top Control Bar */}
        <div className="admin-bookings-controls">
          <div className="admin-controls-left">
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by Booking ID or user name..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={bookingStatusFilter}
              onChange={handleBookingStatusFilterChange}
            >
              <option value="all">All Booking Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              className="admin-filter-select"
              value={paymentStatusFilter}
              onChange={handlePaymentStatusFilterChange}
            >
              <option value="all">All Payment Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>

            <select className="admin-filter-select">
              <option value="">Date Range</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <button className="admin-export-button" onClick={handleExport}>
              <span className="admin-export-icon">📥</span>
              Export CSV
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="admin-empty-state">
            <div className="admin-empty-icon">⏳</div>
            <h3 className="admin-empty-title">Loading bookings...</h3>
            <p className="admin-empty-text">Please wait while we fetch your data</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="admin-empty-state">
            <div className="admin-empty-icon">⚠️</div>
            <h3 className="admin-empty-title">Error loading bookings</h3>
            <p className="admin-empty-text">{error}</p>
            <button 
              className="admin-action-button"
              onClick={() => window.location.reload()}
              style={{ marginTop: '1rem' }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Bookings Table */}
        {!loading && !error && paginatedBookings.length > 0 ? (
          <>
            <div className="admin-bookings-table-wrapper">
              {/* Desktop Table */}
              <table className="admin-bookings-table">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBookings.map((booking) => (
                    <tr key={booking.bookingId || booking.id} className="admin-bookings-table-row">
                      <td className="admin-table-cell admin-table-id">{booking.id}</td>
                      <td className="admin-table-cell">
                        <div style={{ fontWeight: '500' }}>{booking.userName}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>{booking.userEmail}</div>
                      </td>
                      <td className="admin-table-cell admin-table-amount">{booking.amount}</td>
                      <td className="admin-table-cell">
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <span className={`admin-status-badge admin-status-${booking.bookingStatus.toLowerCase()}`}>
                            {booking.bookingStatus}
                          </span>
                          <span className={`admin-status-badge admin-status-${booking.paymentStatus.toLowerCase()}`}>
                            {booking.paymentStatus}
                          </span>
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        <button
                          className="admin-action-button"
                          onClick={() => handleViewDetails(booking)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px'
                          }}
                          title="View Details"
                        >
                          <span>👁️</span>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="admin-bookings-cards">
                {paginatedBookings.map((booking) => (
                  <div key={booking.bookingId || booking.id} className="admin-booking-card">
                    <div className="admin-booking-card-header">
                      <div className="admin-booking-card-id">{booking.id}</div>
                      <div className="admin-booking-card-statuses">
                        <span className={`admin-status-badge admin-status-${booking.bookingStatus.toLowerCase()}`}>
                          {booking.bookingStatus}
                        </span>
                        <span className={`admin-status-badge admin-status-${booking.paymentStatus.toLowerCase()}`}>
                          {booking.paymentStatus}
                        </span>
                      </div>
                    </div>
                    <div className="admin-booking-card-body">
                      <div className="admin-booking-card-row">
                        <span className="admin-booking-card-label">User:</span>
                        <span className="admin-booking-card-value">{booking.userName}</span>
                      </div>
                      <div className="admin-booking-card-row">
                        <span className="admin-booking-card-label">Amount:</span>
                        <span className="admin-booking-card-value admin-booking-card-amount">{booking.amount}</span>
                      </div>
                    </div>
                    <div className="admin-booking-card-actions">
                      <button
                        className="admin-action-button"
                        onClick={() => handleViewDetails(booking)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <span>👁️</span>
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} bookings
              </div>
              <div className="admin-pagination-controls">
                <select
                  className="admin-rows-select"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="admin-page-numbers">
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="admin-page-ellipsis">...</span>}
                          <button
                            className={`admin-page-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : !loading && !error ? (
          /* Empty State */
          <div className="admin-empty-state">
            <div className="admin-empty-icon">🎫</div>
            <h3 className="admin-empty-title">No bookings found</h3>
            <p className="admin-empty-text">
              {searchQuery || bookingStatusFilter !== 'all' || paymentStatusFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'There are no rows in flight_bookings yet, or Supabase returned an empty list. New bookings appear here after a successful checkout save.'}
            </p>
          </div>
        ) : null}

        {/* Details Modal/Popup */}
        {showDetailsModal && selectedBooking && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={handleCloseModal}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '30px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseModal}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px 10px'
                }}
              >
                ×
              </button>

              {/* Modal Header */}
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
                Booking Details - {selectedBooking.id}
              </h2>

              {/* Booking Details Grid */}
              <div style={{ display: 'grid', gap: '20px' }}>
                {/* User Information */}
                <div style={{ borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>User Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <div>
                      <strong>Name:</strong> {selectedBooking.userName}
                    </div>
                    <div>
                      <strong>Email:</strong> {selectedBooking.userEmail}
                    </div>
                    <div>
                      <strong>Contact Email:</strong> {selectedBooking.contactEmail}
                    </div>
                    <div>
                      <strong>Contact Phone:</strong> {selectedBooking.contactPhone}
                    </div>
                  </div>
                </div>

                {/* Flight Information */}
                <div style={{ borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>Flight Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <div>
                      <strong>Route:</strong> {selectedBooking.route}
                    </div>
                    <div>
                      <strong>Flight Number:</strong> {selectedBooking.flightNumber}
                    </div>
                    <div>
                      <strong>Flight Type:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedBooking.flightType}</span>
                    </div>
                    <div>
                      <strong>Cabin Class:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedBooking.cabinClass}</span>
                    </div>
                    <div>
                      <strong>Passengers:</strong> {selectedBooking.passengerCount}
                    </div>
                  </div>
                </div>

                {/* Departure Information */}
                <div style={{ borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>Departure Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <div>
                      <strong>Departure Date:</strong> {selectedBooking.outboundDeparture}
                    </div>
                    <div>
                      <strong>Departure Time:</strong> {selectedBooking.outboundDepartureTime}
                    </div>
                    <div>
                      <strong>Arrival Date:</strong> {selectedBooking.outboundArrival}
                    </div>
                    <div>
                      <strong>Arrival Time:</strong> {selectedBooking.outboundArrivalTime}
                    </div>
                  </div>
                </div>

                {/* Return Information (if round-trip) */}
                {selectedBooking.returnDeparture && (
                  <div style={{ borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>Return Details</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <div>
                        <strong>Return Departure:</strong> {selectedBooking.returnDeparture}
                      </div>
                      <div>
                        <strong>Return Arrival:</strong> {selectedBooking.returnArrival}
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Information */}
                <div style={{ borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>Payment Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <div>
                      <strong>Total Amount:</strong> {selectedBooking.amount}
                    </div>
                    <div>
                      <strong>Base Fare:</strong> ₦{parseFloat(selectedBooking.baseFare || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div>
                      <strong>Taxes:</strong> ₦{parseFloat(selectedBooking.taxes || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div>
                      <strong>Service Fee:</strong> ₦{parseFloat(selectedBooking.serviceFee || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div>
                      <strong>Currency:</strong> {selectedBooking.currency}
                    </div>
                    <div>
                      <strong>Payment Method:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedBooking.paymentMethod}</span>
                    </div>
                    <div>
                      <strong>Payment Status:</strong> 
                      <span className={`admin-status-badge admin-status-${selectedBooking.paymentStatus.toLowerCase()}`} style={{ marginLeft: '8px' }}>
                        {selectedBooking.paymentStatus}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Booking Status */}
                <div style={{ borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>Booking Status</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <div>
                      <strong>Booking Status:</strong>
                      <span className={`admin-status-badge admin-status-${selectedBooking.bookingStatus.toLowerCase()}`} style={{ marginLeft: '8px' }}>
                        {selectedBooking.bookingStatus}
                      </span>
                    </div>
                    <div>
                      <strong>Booking Date:</strong> {selectedBooking.bookingDate}
                    </div>
                  </div>
                </div>

                {/* Passengers Details (if available) */}
                {selectedBooking.rawBooking && selectedBooking.rawBooking.booking_passengers && 
                 Array.isArray(selectedBooking.rawBooking.booking_passengers) && 
                 selectedBooking.rawBooking.booking_passengers.length > 0 && (
                  <div style={{ borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>Passengers</h3>
                    {selectedBooking.rawBooking.booking_passengers.map((passenger, index) => (
                      <div key={passenger.id || index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                          <div><strong>Name:</strong> {passenger.full_name || `${passenger.first_name || ''} ${passenger.last_name || ''}`.trim()}</div>
                          <div><strong>Type:</strong> <span style={{ textTransform: 'capitalize' }}>{passenger.passenger_type}</span></div>
                          {passenger.date_of_birth && <div><strong>Date of Birth:</strong> {new Date(passenger.date_of_birth).toLocaleDateString('en-NG')}</div>}
                          {passenger.passport_number && <div><strong>Passport:</strong> {passenger.passport_number}</div>}
                          {passenger.seat_number && <div><strong>Seat:</strong> {passenger.seat_number}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCloseModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Bookings;

